/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import './MentionsPlugin.scss'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalTypeaheadMenuPlugin, MenuOption, MenuTextMatch, useBasicTypeaheadTriggerMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { TextNode } from 'lexical'
import * as ReactDOM from 'react-dom'
import { $createMentionNode, MentionData } from '../../Nodes/MentionNode/MentionNode'
import classNames from 'classnames'
import { Divider } from '../../../../../../../../components/Divider/Divider'

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']'

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentMentionsRegex.PUNCTUATION

const TRIGGERS = ['@'].join('')

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]'

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')'

const LENGTH_LIMIT = 75

const AtSignMentionsRegex = new RegExp('(^|\\s|\\()(' + '[' + TRIGGERS + ']' + '((?:' + VALID_CHARS + VALID_JOINS + '){0,' + LENGTH_LIMIT + '})' + ')$')

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp('(^|\\s|\\()(' + '[' + TRIGGERS + ']' + '((?:' + VALID_CHARS + '){0,' + ALIAS_LENGTH_LIMIT + '})' + ')$')

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text)

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text)
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1]

    const matchingString = match[3]
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      }
    }
  }
  return null
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 0)
}

class MentionTypeaheadOption extends MenuOption {
  id: string
  label: string
  metadata?: { [key: string]: unknown }

  constructor(key: string, label: string, metadata: { [key: string]: unknown }) {
    super(label)
    this.id = key
    this.label = label
    this.metadata = metadata
  }
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  option: MentionTypeaheadOption
}) {
  return (
    <li
      tabIndex={-1}
      className={classNames('item', { selected: isSelected })}
      ref={option.setRefElement}
      role='option'
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {/* eslint-disable-next-line */}
      <span className='text'>{option.label}</span>
    </li>
  )
}

const CLOSE_OPTION_KEY = '__CLOSE_MENU__'
const CLOSE_OPTION = new MentionTypeaheadOption(CLOSE_OPTION_KEY, 'Close', {})

export type MentionsPluginProps = {
  options: MentionData[]
}
export const MentionsPlugin: React.FC<MentionsPluginProps> = ({ options }) => {
  const [editor] = useLexicalComposerContext()

  const [queryString, setQueryString] = useState<string | null>('')

  const queryOptions = useMemo(() => {
    return options.filter((o) => o.label.toLowerCase().includes(queryString ? queryString.toLowerCase() : '')).map((o) => o as MentionTypeaheadOption)
  }, [queryString, options])

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  })

  const onSelectOption = useCallback(
    (selectedOption: MentionTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      if (selectedOption.id === CLOSE_OPTION_KEY) {
        editor.update(() => {
          if (nodeToReplace) {
            nodeToReplace.remove()
          }
          closeMenu()
        })
        return
      }
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.id, selectedOption.label, selectedOption.metadata)
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode)
        }
        mentionNode.selectEnd()
        closeMenu()
      })
    },
    [editor],
  )

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor)
      if (slashMatch !== null) {
        return null
      }
      return getPossibleQueryMatch(text)
    },
    [checkForSlashTriggerMatch, editor],
  )

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={queryOptions}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorElementRef.current && queryOptions.length
          ? ReactDOM.createPortal(
              <div className='typeahead-popover mentions-menu'>
                <div className='d-flex p-2 justify-content-between'>
                  <div className='bold'>Insert data for Zocks field</div>
                  <div>
                    <i className='bi bi-x cursor-pointer' onClick={() => selectOptionAndCleanUp(CLOSE_OPTION)} />
                  </div>
                </div>
                <Divider />
                <ul>
                  {queryOptions.map((option: MentionTypeaheadOption, i: number) => (
                    <MentionsTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i)
                        selectOptionAndCleanUp(option)
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i)
                      }}
                      key={option.id}
                      option={option}
                    />
                  ))}
                </ul>
              </div>,
              anchorElementRef.current,
            )
          : null
      }
    />
  )
}
